/**
 * Job Openings Component
 * Admin → Recruitment → Job Openings
 * 
 * Features:
 * - List and manage job openings
 * - Create/Edit jobs with role, team, hiring manager
 * - View candidate counts per job
 */

import React, { useState, useEffect, useCallback } from 'react';
import { isHrReadOnly } from '../../../lib/hrRole';
import axios from 'axios';
import {
  Briefcase,
  Plus,
  Search,
  Edit2,
  Eye,
  Users,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Building,
  User,
  Calendar
} from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { RoleSelect } from '../hr/RoleSelect';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../ui/dialog';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import RecruitmentNav from './RecruitmentNav';

const API = process.env.REACT_APP_BACKEND_URL || '';

const JobOpenings = () => {
  const hrReadOnly = isHrReadOnly();
  const navigate = useNavigate();
  
  // Data state
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [stats, setStats] = useState(null);

  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [teamFilter, setTeamFilter] = useState('');

  // Hover state
  const [hoveredRow, setHoveredRow] = useState(null);

  // Options
  const [options, setOptions] = useState({
    roles: [],
    teams: [],
    managers: [],
    statuses: []
  });

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    role: '',
    team: '',
    hiring_manager_id: '',
    positions: 1,
    description: '',
    status: 'open'
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const limit = 25;

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  };

  // Fetch options
  const fetchOptions = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      const [roles, teams, managers, statuses] = await Promise.all([
        axios.get(`${API}/api/hr/options/roles`, { headers }),
        axios.get(`${API}/api/hr/options/teams`, { headers }),
        axios.get(`${API}/api/hr/options/managers`, { headers }),
        axios.get(`${API}/api/recruitment/options/job-statuses`, { headers })
      ]);

      setOptions({
        roles: roles.data.roles || [],
        teams: teams.data.teams || [],
        managers: managers.data.managers || [],
        statuses: statuses.data.statuses || []
      });
    } catch (err) {
      console.error('Error fetching options:', err);
    }
  }, []);

  // Fetch jobs
  const fetchJobs = useCallback(async () => {
    try {
      setLoading(true);
      const headers = getAuthHeaders();

      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', limit);
      if (searchQuery) params.append('search', searchQuery);
      if (statusFilter) params.append('status', statusFilter);
      if (teamFilter) params.append('team', teamFilter);

      const response = await axios.get(`${API}/api/recruitment/jobs?${params.toString()}`, { headers });
      
      setJobs(response.data.data || []);
      setTotal(response.data.total || 0);
      setTotalPages(response.data.pages || 1);
    } catch (err) {
      console.error('Error fetching jobs:', err);
      if (err.response?.status === 403) {
        toast.error('Access denied. Recruitment is admin/team-lead only.');
      } else {
        toast.error('Failed to load jobs');
      }
    } finally {
      setLoading(false);
    }
  }, [page, searchQuery, statusFilter, teamFilter]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      const response = await axios.get(`${API}/api/recruitment/stats`, { headers });
      setStats(response.data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  }, []);

  useEffect(() => {
    fetchOptions();
    fetchStats();
  }, [fetchOptions, fetchStats]);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Modal handlers
  const handleCreate = () => {
    setEditingJob(null);
    setFormData({
      title: '',
      role: '',
      team: '',
      hiring_manager_id: '',
      positions: 1,
      description: '',
      status: 'open'
    });
    setFormErrors({});
    setIsModalOpen(true);
  };

  const handleEdit = (job) => {
    setEditingJob(job);
    setFormData({
      title: job.title || '',
      role: job.role || '',
      team: job.team || '',
      hiring_manager_id: job.hiring_manager_id || '',
      positions: job.positions || 1,
      description: job.description || '',
      status: job.status || 'open'
    });
    setFormErrors({});
    setIsModalOpen(true);
  };

  // HR-RECRUIT-ROLE-001: teams come from the Teams collection; match stored values by name/short/code
  const teamOptionValue = (team) => {
    if (!team) return '';
    const k = String(team).trim().toLowerCase();
    const hit = options.teams.find(t => t.value.toLowerCase() === k || (t.aliases || []).some(a => a.toLowerCase() === k));
    return hit ? hit.value : team;
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.title?.trim()) errors.title = 'Job title is required';
    if (!formData.role) errors.role = 'Role is required';
    else if (formData.role.length < 2) errors.role = 'Role must be at least 2 characters';
    if (!formData.team) errors.team = 'Team is required';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setSubmitting(true);
      const headers = getAuthHeaders();

      if (editingJob) {
        await axios.put(
          `${API}/api/recruitment/jobs/${editingJob.job_id}`,
          formData,
          { headers }
        );
        toast.success('Job updated successfully');
      } else {
        await axios.post(`${API}/api/recruitment/jobs`, formData, { headers });
        toast.success('Job created successfully');
      }

      setIsModalOpen(false);
      fetchJobs();
      fetchStats();
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to save job';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  // Status badge
  const StatusBadge = ({ status }) => {
    const styles = {
      open: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      on_hold: 'bg-amber-50 text-amber-700 border-amber-200',
      closed: 'bg-slate-100 text-slate-600 border-slate-200',
      filled: 'bg-blue-50 text-blue-700 border-blue-200'
    };

    const labels = {
      open: 'Open',
      on_hold: 'On Hold',
      closed: 'Closed',
      filled: 'Filled'
    };

    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${styles[status] || styles.closed}`}>
        {labels[status] || status}
      </span>
    );
  };

  // KPI Card
  const KPICard = ({ icon: Icon, value, label, accent }) => {
    const accentStyles = {
      blue: 'from-blue-50 to-slate-50 border-blue-100',
      green: 'from-emerald-50 to-slate-50 border-emerald-100',
      purple: 'from-purple-50 to-slate-50 border-purple-100'
    };

    const iconStyles = {
      blue: 'bg-blue-100 text-blue-600',
      green: 'bg-emerald-100 text-emerald-600',
      purple: 'bg-purple-100 text-purple-600'
    };

    return (
      <div className={`bg-gradient-to-br ${accentStyles[accent]} rounded-xl border p-5 transition-all hover:shadow-md`}>
        <div className="flex items-start justify-between">
          <div>
            <p className="text-3xl font-bold text-slate-900 tracking-tight">{value}</p>
            <p className="text-sm text-slate-500 mt-1 font-medium">{label}</p>
          </div>
          <div className={`w-11 h-11 ${iconStyles[accent]} rounded-xl flex items-center justify-center`}>
            <Icon className="w-5 h-5" />
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <RecruitmentNav />

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Job Openings</h2>
          <p className="text-sm text-slate-500 mt-1">
            Manage open positions and track hiring progress
          </p>
        </div>
        {!hrReadOnly && (
          <Button onClick={handleCreate} className="gap-2 shadow-sm" data-testid="create-job-btn">
            <Plus className="w-4 h-4" />
            Create Job
          </Button>
        )}
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-3 gap-4">
          <KPICard
            icon={Briefcase}
            value={stats.jobs?.open || 0}
            label="Open Positions"
            accent="green"
          />
          <KPICard
            icon={Users}
            value={stats.candidates?.total || 0}
            label="Total Candidates"
            accent="blue"
          />
          <KPICard
            icon={User}
            value={stats.recent_hires || 0}
            label="Recent Hires (30d)"
            accent="purple"
          />
        </div>
      )}

      {/* Filters */}
      <div className="bg-slate-50/80 backdrop-blur-sm rounded-2xl border border-slate-200/60 p-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex-1 min-w-[240px]">
            <div className="relative">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by job title..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-white border-slate-200 rounded-xl h-10"
              />
            </div>
          </div>
          
          <div className="hidden sm:block w-px h-8 bg-slate-200" />
          
          <Select value={statusFilter || 'all'} onValueChange={(v) => { setStatusFilter(v === 'all' ? '' : v); setPage(1); }}>
            <SelectTrigger className="w-[140px] bg-white border-slate-200 rounded-xl h-10">
              <SelectValue placeholder="All Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              {options.statuses.map(s => (
                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={teamFilter || 'all'} onValueChange={(v) => { setTeamFilter(v === 'all' ? '' : v); setPage(1); }}>
            <SelectTrigger className="w-[220px] bg-white border-slate-200 rounded-xl h-10" data-testid="job-team-filter">
              <SelectValue placeholder="All Teams" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Teams</SelectItem>
              {options.teams.map(t => (
                <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {(searchQuery || statusFilter || teamFilter) && (
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => { setSearchQuery(''); setStatusFilter(''); setTeamFilter(''); setPage(1); }}
              className="text-slate-500 hover:text-slate-700"
            >
              Clear
            </Button>
          )}
        </div>
      </div>

      {/* Results */}
      <div className="flex items-center justify-between px-1">
        <p className="text-sm text-slate-500">
          Showing <span className="font-semibold text-slate-700">{jobs.length}</span> of <span className="font-semibold text-slate-700">{total}</span> jobs
        </p>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-24">
            <div className="text-center">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-3" />
              <p className="text-sm text-slate-500">Loading jobs...</p>
            </div>
          </div>
        ) : jobs.length === 0 ? (
          <div className="text-center py-24">
            <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Briefcase className="w-8 h-8 text-slate-400" />
            </div>
            <h4 className="text-lg font-semibold text-slate-700 mb-2">No Job Openings</h4>
            <p className="text-slate-500 max-w-sm mx-auto mb-6">
              Create your first job opening to start recruiting.
            </p>
            {!hrReadOnly && (
              <Button onClick={handleCreate} className="gap-2">
                <Plus className="w-4 h-4" />
                Create First Job
              </Button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="tf-thead">
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Job</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Team</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Hiring Manager</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Candidates</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider w-24">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {jobs.map((job) => (
                  <tr 
                    key={job.job_id} 
                    className={`transition-colors cursor-pointer ${hoveredRow === job.job_id ? 'bg-slate-50' : 'bg-white'}`}
                    onMouseEnter={() => setHoveredRow(job.job_id)}
                    onMouseLeave={() => setHoveredRow(null)}
                    onClick={() => navigate(`/admin/recruitment/candidates?job_id=${job.job_id}`)}
                  >
                    <td className="px-6 py-4">
                      <div>
                        <p className="font-semibold text-slate-900">{job.title}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-xs text-slate-400 font-mono">{job.job_id}</span>
                          <span className="text-slate-300">·</span>
                          <span className="text-xs text-slate-500 capitalize">{job.role?.replace(/_/g, ' ')}</span>
                          {job.positions > 1 && (
                            <>
                              <span className="text-slate-300">·</span>
                              <span className="text-xs text-blue-600">{job.positions} positions</span>
                            </>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center gap-1.5 text-sm text-slate-700">
                        <Building className="w-3.5 h-3.5 text-slate-400" />
                        {job.team}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {job.hiring_manager_name ? (
                        <p className="text-sm text-slate-700">{job.hiring_manager_name}</p>
                      ) : (
                        <span className="text-sm text-slate-400 italic">Not Assigned</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-slate-700">{job.total_candidates || 0}</span>
                        {job.candidate_counts?.interview > 0 && (
                          <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">
                            {job.candidate_counts.interview} interviewing
                          </span>
                        )}
                        {job.candidate_counts?.offer > 0 && (
                          <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                            {job.candidate_counts.offer} in offer
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <StatusBadge status={job.status} />
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className={`flex items-center justify-end gap-1 transition-opacity ${hoveredRow === job.job_id ? 'opacity-100' : 'opacity-0'}`}>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="h-8 w-8 p-0 hover:bg-blue-50 hover:text-blue-600"
                          onClick={(e) => { e.stopPropagation(); navigate(`/admin/recruitment/candidates?job_id=${job.job_id}`); }}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="h-8 w-8 p-0 hover:bg-slate-100"
                          style={hrReadOnly ? { display: 'none' } : undefined}
                          onClick={(e) => { e.stopPropagation(); handleEdit(job); }}
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 bg-slate-50/50 border-t border-slate-100 flex items-center justify-between">
            <p className="text-sm text-slate-500">
              Page <span className="font-semibold text-slate-700">{page}</span> of <span className="font-semibold text-slate-700">{totalPages}</span>
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="h-8"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="h-8"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl">
              {editingJob ? 'Edit Job Opening' : 'Create Job Opening'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">
                Job Title <span className="text-red-500">*</span>
              </label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                placeholder="e.g., Senior Title Processor"
                className={formErrors.title ? 'border-red-500' : ''}
              />
              {formErrors.title && <p className="text-xs text-red-500 mt-1">{formErrors.title}</p>}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">
                  Role <span className="text-red-500">*</span>
                </label>
                <RoleSelect value={formData.role} onChange={(v) => setFormData(prev => ({ ...prev, role: v }))} options={options.roles} error={formErrors.role} testId="job-role" />
                {formErrors.role && <p className="text-xs text-red-500 mt-1">{formErrors.role}</p>}
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">
                  Team <span className="text-red-500">*</span>
                </label>
                <Select value={teamOptionValue(formData.team)} onValueChange={(v) => setFormData(prev => ({ ...prev, team: v }))}>
                  <SelectTrigger className={formErrors.team ? 'border-red-500' : ''} data-testid="job-team-trigger">
                    <SelectValue placeholder="Select team" />
                  </SelectTrigger>
                  <SelectContent>
                    {options.teams.map(t => (
                      <SelectItem key={t.value} value={t.value} data-testid={`job-team-opt-${t.value.replace(/\W+/g, '-').toLowerCase()}`}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {formErrors.team && <p className="text-xs text-red-500 mt-1">{formErrors.team}</p>}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Hiring Manager</label>
                <Select 
                  value={formData.hiring_manager_id || 'none'} 
                  onValueChange={(v) => setFormData(prev => ({ ...prev, hiring_manager_id: v === 'none' ? '' : v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select manager" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Not Assigned</SelectItem>
                    {options.managers.map(m => (
                      <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Positions</label>
                <Input
                  type="number"
                  min="1"
                  max="100"
                  value={formData.positions}
                  onChange={(e) => setFormData(prev => ({ ...prev, positions: parseInt(e.target.value) || 1 }))}
                />
              </div>
            </div>

            {editingJob && (
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Status</label>
                <Select value={formData.status} onValueChange={(v) => setFormData(prev => ({ ...prev, status: v }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {options.statuses.map(s => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Description</label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Job description (optional)"
              />
            </div>
          </div>

          <DialogFooter className="gap-2 pt-4">
            <Button variant="outline" onClick={() => setIsModalOpen(false)}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={submitting}>
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Saving...
                </>
              ) : (
                editingJob ? 'Save Changes' : 'Create Job'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default JobOpenings;
